import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
  Toolbar,
} from "@mui/x-data-grid-premium";
import { useFetch } from "../../../../services/hooks/useFetch";
import AddIcon from "@mui/icons-material/Add";
import { apiPrefix } from "../../../../utils/main";
import LabGroupDetails from "./LabGroupDetails";
import { useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";

export default function LabGroupsPage() {
  function newLabGroup() {
    searchParams.set("mode", "create");
    setLabGroup({});
    window.history.pushState({}, "", "?" + searchParams.toString());
  }

  function CustomToolBar() {
    return (
      <Toolbar>
        <Button
          endIcon={<AddIcon />}
          size="medium"
          onClick={() => newLabGroup()}
        >
          NEW GROUP
        </Button>
      </Toolbar>
    );
  }

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("");
  const [shouldFetchLabs, setShouldFetchLabs] = useState(true);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [labGroup, setLabGroup] = useState<
    LabGroup | Partial<LabGroup> | undefined
  >(undefined);

  const {
    data: labGroupsList,
    fetch: fetchLabGroups,
    isLoading: labGroupsLoading,
  } = useFetch<PagedResponse<LabGroup>>();

  const labGroupTableFields: GridColDef<LabGroup>[] = [
    {
      field: "name",
      valueGetter: (_value, row) => {
        return row.name;
      },
      headerName: "Group Name",
      width: 400,
    },

    {
      field: "inUse",
      renderCell: (params) =>
        params.row.inUse ? <CheckIcon fontSize={"small"} /> : "",
      align: "center",
      headerAlign: "center",
      display: "flex",
      headerName: "In use",
      width: 75,
    },
    {
      field: "sourceSchemaIdentifier",
      valueGetter: (_value, row) => {
        return row.schemaIdentifier;
      },
      headerName: "Schema",
      width: 250,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      flex: 1,
      align: "center",
      getActions: ({ row }: GridRowParams<LabGroup>) => {
        return [
          <GridActionsCellItem
            key={row.id}
            icon={<ChevronRightIcon fontSize="small" />}
            label="View Lab Details"
            className="textPrimary"
            onClick={() => {
              searchParams.set("name", row.name);
              searchParams.set("mode", "modify");
              setSearchParams(searchParams);
              setLabGroup(row);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (shouldFetchLabs && !labGroupsLoading) {
      const params = new URLSearchParams();
      params.set("page", paginationModel.page.toString());
      params.set("limit", paginationModel.pageSize.toString());
      if (sortModel.length > 0) {
        params.set(
          "sort",
          sortModel
            .map((s) => `${s.sort === "desc" ? "-" : "+"}${s.field}`)
            .join(","),
        );
      }
      if (filter !== "") {
        params.set("filter", filter);
      }
      void fetchLabGroups(`${apiPrefix}/labs?${params.toString()}`);
      setShouldFetchLabs(false);
    }
  }, [
    shouldFetchLabs,
    fetchLabGroups,
    labGroupsLoading,
    paginationModel,
    sortModel,
    filter,
  ]);

  return (
    <Grid
      container
      direction="column"
      className="company-settings-item"
      sx={{ flex: 1 }}
    >
      {labGroup &&
      (searchParams.get("mode") === "create" ||
        searchParams.get("mode") === "modify") ? (
        <LabGroupDetails labGroup={labGroup} />
      ) : (
        <div
          style={{
            height: "100%",
            minHeight: "400px",
            maxWidth: "calc(90vw - 15px)",
          }}
        >
          <Box className={"lab-groups"}>
            <Grid>
              <Typography variant="h4" textAlign="left">
                Lab Groups
              </Typography>
            </Grid>
            <br />
            <Grid>
              <Typography variant="body2">Manage Lab groups</Typography>
            </Grid>
            <br />

            <DataGridPremium
              disableColumnFilter
              disableColumnResize
              disableAggregation
              disableRowGrouping
              disablePivoting
              disableColumnPinning
              pageSizeOptions={[5, 10, 20]}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model: GridPaginationModel) => {
                setShouldFetchLabs(true);
                setPaginationModel(model);
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(model: GridSortModel) => {
                setShouldFetchLabs(true);
                setSortModel(model);
              }}
              filterMode="server"
              onFilterModelChange={(filterModel) => {
                setShouldFetchLabs(true);
                setFilter(filterModel.quickFilterValues?.join(" ") || "");
              }}
              rowCount={labGroupsList?.rowCount || 0}
              loading={labGroupsLoading}
              showToolbar={true}
              slots={{
                toolbar: CustomToolBar,
              }}
              slotProps={{
                loadingOverlay: {
                  variant: "linear-progress",
                },
              }}
              columnHeaderHeight={60}
              rowHeight={60}
              getRowId={(row) => row.id}
              density="compact"
              columns={labGroupTableFields}
              rows={labGroupsList?.results || []}
              rowSelection={false}
            />
          </Box>
        </div>
      )}
    </Grid>
  );
}
